import classNames from "classnames";
import * as React from "react";

import {
  ResourceView,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { FormattedDateTime } from "@/shared/datetime";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  isTopicStatsRefusal,
  sessionTopicDurationNs,
  sessionTopicFrequencyHz,
  type SessionTopicRecord,
  type SessionTopicStatsRun,
} from "@/shared/domain/sessions";
import {
  useAllSessionTopics,
  useSessionIngestionStatus,
  useSessionTopicStatsRun,
} from "@/shared/domain/sessions/hooks";
import { Banner, SearchInput } from "@/shared/foundation/primitives";
import {
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  TimestampCell,
  timestampCellClass,
  useInMemoryRows,
  type ColDef,
  type ColumnPickerOption,
  type InMemorySortKey,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";
import { isSubstringMatch } from "@/shared/text/substringFilter";
import { formatShortNanoDuration } from "@/shared/time/formatDuration";
import { nanoSecToDate } from "@/shared/time/time";

import { SessionTopicPanel } from "./SessionTopicPanel";
import { useSessionOutletContext } from "./useSessionOutletContext";

const SCHEMA_FIELD = "schema";

/** The Schema column: the topic's schema, or how many it is recorded with. */
function schemaLabel({ schemas }: SessionTopicRecord): string | null {
  if (schemas.length === 0) {
    return null;
  }
  return schemas.length === 1 ? schemas[0].name : `${schemas.length} schemas`;
}

function nsToDate(ns: bigint | null): Date | null {
  return ns === null ? null : nanoSecToDate(ns);
}

const countFormat = new Intl.NumberFormat();
const rateFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});
const slowRateFormat = new Intl.NumberFormat(undefined, {
  maximumSignificantDigits: 2,
});

function formatFrequency(hz: number): string {
  return `${(hz < 1 ? slowRateFormat : rateFormat).format(hz)} Hz`;
}

const columnHelper = createColumnHelper<SessionTopicRecord>();

const COLUMNS: ColDef<SessionTopicRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 200,
    sortable: true,
    cellClass: classNames(cellStyles.primaryContent, cellStyles.monoCell),
  }),
  columnHelper.accessor(schemaLabel, {
    colId: SCHEMA_FIELD,
    headerName: "Schema",
    flex: 1.5,
    minWidth: 160,
    sortable: true,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor(sessionTopicDurationNs, {
    colId: "duration",
    headerName: "Duration",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) => formatShortNanoDuration(params.value),
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor((topic) => nsToDate(topic.start_time_ns), {
    colId: "start",
    headerName: "Start",
    minWidth: 160,
    sortable: true,
    cellRenderer: TimestampCell,
    cellClass: timestampCellClass,
  }),
  columnHelper.accessor((topic) => nsToDate(topic.end_time_ns), {
    colId: "end",
    headerName: "End",
    minWidth: 160,
    sortable: true,
    cellRenderer: TimestampCell,
    cellClass: timestampCellClass,
  }),
  columnHelper.accessor((topic) => topic.message_count, {
    colId: "messages",
    type: "rightAligned",
    headerName: "Messages",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) =>
      params.value === null || params.value === undefined
        ? ""
        : countFormat.format(params.value),
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor(sessionTopicFrequencyHz, {
    colId: "frequency",
    type: "rightAligned",
    headerName: "Frequency",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) =>
      params.value === null || params.value === undefined
        ? ""
        : formatFrequency(params.value),
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("topic_id", {
    headerName: "Topic ID",
    minWidth: 180,
  }),
];

/** The columns that come from the published `message_count` stat. */
const STATS_COLUMN_IDS = new Set(["messages", "frequency"]);

/**
 * Topic ID is offered in the Columns menu but starts hidden. The stats columns
 * are seeded visible even while the flag is off, so they show when the flag
 * finishes loading after the tab mounts.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = COLUMNS.map(colId).filter(
  (id) => id !== "topic_id",
);

interface ColumnSet {
  columns: ColDef<SessionTopicRecord>[];
  pickerOptions: ColumnPickerOption[];
}

function columnSet(columns: ColDef<SessionTopicRecord>[]): ColumnSet {
  return {
    columns,
    pickerOptions: columns.map((column) => ({
      id: colId(column),
      label: column.headerName ?? colId(column),
    })),
  };
}

const WITH_STATS = columnSet(COLUMNS);
const WITHOUT_STATS = columnSet(
  COLUMNS.filter((column) => !STATS_COLUMN_IDS.has(colId(column))),
);

/**
 * How each sortable column compares two topics. The listing takes no sort
 * parameter and `<DataTable>` sorts server-side by contract, so the rows are
 * sorted here; keep this map and the `sortable: true` flags in step.
 */
const SORT_KEYS: Record<string, InMemorySortKey<SessionTopicRecord>> = {
  name: (topic) => topic.name,
  [SCHEMA_FIELD]: schemaLabel,
  duration: (topic) => {
    const duration = sessionTopicDurationNs(topic);
    return duration === null ? null : Number(duration);
  },
  start: (topic) => nsToDate(topic.start_time_ns),
  end: (topic) => nsToDate(topic.end_time_ns),
  messages: (topic) => topic.message_count,
  frequency: sessionTopicFrequencyHz,
};

/**
 * Whether `topic` passes the schema filter: "contains" keeps a topic recorded
 * with any of the chosen schemas, "does not contain" one recorded with none
 * of them. A filter on any other field keeps every topic.
 */
function matchesFilter(topic: SessionTopicRecord, filter: Filter): boolean {
  if (filter.field !== SCHEMA_FIELD || filter.type !== "set") {
    return true;
  }
  const chosen = new Set(filter.values.map((option) => option.value));
  if (chosen.size === 0) {
    return true;
  }
  const recordedWithChosen = topic.schemas.some((schema) =>
    chosen.has(schema.name),
  );
  return filter.comparator === "NOT_CONTAINS"
    ? !recordedWithChosen
    : recordedWithChosen;
}

function matchesFilters(
  topic: SessionTopicRecord,
  filters: readonly Filter[],
  matchMode: FilterMatchMode,
): boolean {
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((filter) => matchesFilter(topic, filter))
    : filters.every((filter) => matchesFilter(topic, filter));
}

/**
 * What the Topics tab says about the latest topic stats run: that it is
 * scheduled or running, or that it failed. Nothing for a run that finished or
 * never ran.
 */
function TopicStatsRunBanner({ run }: { run: SessionTopicStatsRun }) {
  if (run.status === "not_run" || run.status === "finished") {
    return null;
  }
  const invocationLink = run.invocation_id !== null && (
    <RobotoLinkHoverUnderline
      to={Goto.createInvocationRoute(run.invocation_id)}
    >
      View invocation
    </RobotoLinkHoverUnderline>
  );
  const started =
    run.started === null ? (
      "The run starts shortly. "
    ) : (
      <>
        Started <FormattedDateTime value={new Date(run.started)} />.{" "}
      </>
    );
  return run.status !== "failed" ? (
    <Banner
      title="Topic stats are being generated"
      description={
        <>
          {started}Message counts, frequencies and field stats update when the
          run finishes. {invocationLink}
        </>
      }
    />
  ) : (
    <Banner
      variant="warning"
      title="Topic stats could not be generated"
      description={
        <>
          {started}The latest run failed, so the stats shown may be missing or
          out of date. {invocationLink}
        </>
      }
    />
  );
}

/**
 * Topics tab of the session-detail page. Lists every topic whose data the
 * session takes, with its schema, time span in the session, message count and
 * frequency, searchable by name and filterable by schema. Expanding a row
 * lists the topic's fields and their stats. Message count, frequency, the
 * field stats and the state of the latest topic stats run appear only while
 * `release-session-topic-stats` is on for the session's org.
 *
 * The listing has no sort or filter parameters, so every page of it is
 * fetched and the rows are filtered, sorted and paged here.
 */
export function TopicsTab() {
  const { session } = useSessionOutletContext();
  const query = useAllSessionTopics(session.session_id);
  // The routes gate topic stats on the session's org, but this flag check also
  // passes for a user in a rollout group. It only saves the requests when the
  // flag is off; the run route's refusal decides.
  const flagOn = useCheckFeatureFlag(
    "release-session-topic-stats",
    session.org_id,
  );
  const ingestion = useSessionIngestionStatus({ session, enabled: flagOn });
  const run = useSessionTopicStatsRun({
    sessionId: session.session_id,
    announcements: Math.max(
      session.ingestion_count,
      ingestion.data?.ingestion_count ?? 0,
    ),
    enabled: flagOn,
  });
  const statsShown = flagOn && !isTopicStatsRefusal(run.error);
  const { columns, pickerOptions } = statsShown ? WITH_STATS : WITHOUT_STATS;

  const view = useResourceView<SessionTopicRecord>({
    getRowId: (topic) => topic.topic_id,
    initialSort: { columnId: "name", direction: "asc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  const topics = React.useMemo(() => query.data ?? [], [query.data]);

  const filterDefinitions = React.useMemo<FilterDefinition[]>(() => {
    const schemaNames = new Set(
      topics.flatMap((topic) => topic.schemas.map((schema) => schema.name)),
    );
    return [
      {
        type: "set",
        field: SCHEMA_FIELD,
        name: "Schema",
        options: [...schemaNames].sort((a, b) => a.localeCompare(b)),
      },
    ];
  }, [topics]);

  const [searchText, setSearchText] = React.useState("");
  const filteredTopics = React.useMemo(() => {
    const search = searchText.trim();
    const named =
      search === ""
        ? topics
        : topics.filter((topic) => isSubstringMatch(search, topic.name));
    if (view.filters.type !== "structured") {
      return named;
    }
    const { filters, matchMode } = view.filters;
    return named.filter((topic) => matchesFilters(topic, filters, matchMode));
  }, [topics, searchText, view.filters]);

  const { visibleRows, totalRowCount, hasNextPage } = useInMemoryRows(
    view.table,
    filteredTopics,
    SORT_KEYS,
  );

  const renderExpandedRow = React.useCallback(
    (record: SessionTopicRecord) => (
      <SessionTopicPanel
        topic={record}
        sessionId={session.session_id}
        orgId={session.org_id}
        statsShown={statsShown}
      />
    ),
    [session.session_id, session.org_id, statsShown],
  );

  if (query.isError) {
    return (
      <Banner variant="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load topics. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Banner>
    );
  }

  return (
    <ResourceView.Root>
      {statsShown && run.error !== null && (
        <Banner variant="error">
          {userFacingErrorMessage(
            run.error,
            "Failed to check the latest topic stats run.",
          )}
        </Banner>
      )}
      {statsShown && run.data !== undefined && (
        <TopicStatsRunBanner run={run.data} />
      )}
      <ResourceView.Actions>
        <SearchInput
          placeholder="Search topics"
          aria-label="Search topics"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <ResourceView.Filters
          filterDefinitions={filterDefinitions}
          onFilterSelected={view.commit}
        />
        <ColumnVisibility
          options={pickerOptions}
          visibleColumnIds={view.table.visibleColumnIds}
          setColumnVisibility={view.table.setColumnVisibility}
        />
      </ResourceView.Actions>
      <ResourceView.ActiveFilters
        filters={view.filters}
        filterDefinitions={filterDefinitions}
        onRemove={view.removeFilter}
        onUpdate={view.updateFilter}
        onClear={view.clearFilters}
        onMatchModeChange={view.setMatchMode}
        onFilterSelected={view.commit}
      />
      <DataTable<SessionTopicRecord>
        defaultColDef={FLEX_FILL_COL_DEF}
        table={view.table}
        columns={columns}
        data={visibleRows}
        totalRowCount={totalRowCount}
        hasNextPage={hasNextPage}
        loading={deriveLoadingState(query)}
        noRowsContent={
          view.hasActiveFilters || searchText.trim() !== ""
            ? "No topics match your search or filters"
            : "No topics in this session yet"
        }
        expansion={{ renderRow: renderExpandedRow }}
      />
    </ResourceView.Root>
  );
}
